import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTolgee } from '@tolgee/react';

interface OrderDetailsNavProps {
	tabs: React.ReactNode;
	renderActions?: (compactLevel: number) => React.ReactNode;
	maxCompactLevel?: number;
}

const OrderDetailsNav: React.FC<OrderDetailsNavProps> = ({
	tabs,
	renderActions,
	maxCompactLevel = 0,
}) => {
	const navRef = useRef<HTMLElement>(null);
	const tabsRef = useRef<HTMLDivElement>(null);
	const language = useTolgee(['language']).getLanguage();
	const [tabsWidth, setTabsWidth] = useState<number>(0);

	const key = `${language}|${tabsWidth}`;
	const [fit, setFit] = useState<{ key: string; level: number }>({
		key,
		level: 0,
	});
	const level = fit.key === key ? fit.level : 0;

	useLayoutEffect(() => {
		const nav = navRef.current;
		if (!nav) return;
		const overflowing = nav.scrollWidth > nav.clientWidth + 1;
		if (overflowing && level < maxCompactLevel) {
			setFit({ key, level: level + 1 });
		} else if (fit.key !== key) {
			setFit({ key, level });
		}
	}, [key, level, fit.key, maxCompactLevel]);

	useEffect(() => {
		const element = tabsRef.current;
		if (!element || typeof ResizeObserver === 'undefined') return;
		const observer = new ResizeObserver(([entry]) =>
			setTabsWidth(Math.round(entry.contentRect.width)),
		);
		observer.observe(element);
		return () => observer.disconnect();
	}, []);

	const actions = renderActions?.(level);

	return (
		<nav
			ref={navRef}
			className="flex flex-nowrap items-center px-10 py-7 overflow-hidden"
		>
			<div
				ref={tabsRef}
				className="flex flex-none items-center gap-[3px] [&>*]:w-auto"
			>
				{tabs}
			</div>
			{actions ? (
				<div className="ml-auto flex flex-none items-center gap-[3px] pl-[3px]">
					{actions}
				</div>
			) : null}
		</nav>
	);
};

export default OrderDetailsNav;
